import { reportError } from "src/lib/observability/reportError";
import { useMemo, useRef, useState } from "react";
import { Modal, Chip, toast } from "@heroui/react";
import { Button } from "src/components/button";
import { modalApi } from "src/infra/modals";
import { FirebaseAPI, TOrder, TOrganization } from "@jsdev_ninja/core";
import { useApiState } from "src/appApi/useApiState";
import { FirebaseApi } from "src/lib/firebase";
import type { InvoiceRow } from "src/pages/admin/AdminCustomerInvoicesPage/useAdminCustomerInvoices";

const VAT_PCT = 18;

type SellerIdentity = { name: string; address?: string; email?: string; companyNumber?: string };

/**
 * Registered legal business details shown in the invoice header, keyed by store id.
 * Mirrors `getSellerIdentity` in the functions document templates so the in-app
 * preview matches the generated PDF. Falls back to the store document fields.
 */
const SELLER_IDENTITIES: Partial<Record<string, SellerIdentity>> = {
	balasistore_store: {
		name: "בלסי סטור בע״מ",
		address: "שונצינו 1, תל אביב (אין קבלת קהל — שירות אונליין בלבד)",
		email: "balasistore5@gmail.com",
		companyNumber: "516127321",
	},
};

function getSellerIdentity(store?: { id?: string; name?: string; companyNumber?: string }): SellerIdentity {
	const override = store?.id ? SELLER_IDENTITIES[store.id] : undefined;
	return {
		name: override?.name ?? store?.name ?? "",
		address: override?.address,
		email: override?.email,
		companyNumber: override?.companyNumber ?? store?.companyNumber,
	};
}

function fmtMoney(n: number): string {
	return "₪" + n.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(ms?: number): string {
	if (!ms) return "—";
	const v = ms < 1e12 ? ms * 1000 : ms;
	const d = new Date(v);
	const dd = String(d.getDate()).padStart(2, "0");
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	return `${dd}.${mm}.${d.getFullYear()}`;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Per-order VAT breakdown derived from the cart (product.vat marks liable items). */
function orderVatBreakdown(order: TOrder) {
	const total = order.cart?.cartTotal ?? 0;
	const vat = order.cart?.cartVat ?? 0;
	const items = order.cart?.items ?? [];
	const exempt = items.reduce((sum, item) => {
		if (item?.product && item.product.vat === false) {
			const unit = item.finalPrice ?? item.product.price ?? 0;
			return sum + unit * (item.amount ?? 0);
		}
		return sum;
	}, 0);
	const vatBase = Math.max(0, round2(total - vat - exempt));
	return { total: round2(total), vat: round2(vat), exempt: round2(exempt), vatBase };
}

// Exact document styling ported from the approved demo (admin.css dn-* rules).
const DOC_CSS = `
.dn-document{background:#fff;color:#1a1a17;line-height:1.5;padding:0;direction:rtl;text-align:right}
.dn-doc-head{display:grid;grid-template-columns:1fr auto;gap:24px;align-items:flex-start;padding:28px 32px 22px;background:linear-gradient(135deg,#0d4f3a 0%,#1b7a3d 100%);color:#fff;position:relative;overflow:hidden}
.dn-doc-head::before{content:"";position:absolute;top:-60px;left:-60px;width:200px;height:200px;border-radius:50%;background:rgba(232,128,74,.18)}
.dn-doc-head-l{display:flex;gap:18px;align-items:flex-start;position:relative;z-index:1}
.dn-logo{height:87px;width:240px;object-fit:contain;background:#fff;padding:6px 10px;border-radius:6px;filter:drop-shadow(0 2px 6px rgba(0,0,0,.18))}
.dn-sender{display:flex;flex-direction:column;gap:1px;font-size:12px;line-height:1.5}
.dn-sender b{font-size:16px;font-weight:800;letter-spacing:-.02em;margin-bottom:3px}
.dn-sender span{opacity:.85;font-weight:400}
.dn-doc-head-r{text-align:left;display:flex;flex-direction:column;gap:4px;position:relative;z-index:1;min-width:230px}
.dn-doc-title{font-size:24px;font-weight:900;letter-spacing:-.04em;line-height:1;border-right:4px solid #e8804a;padding-right:12px}
.dn-doc-num{font-family:monospace;font-size:13px;letter-spacing:.5px;background:rgba(255,255,255,.15);padding:4px 10px;display:inline-block;width:fit-content;margin:6px 0 4px auto;border:1px solid rgba(255,255,255,.25)}
.dn-doc-meta{display:flex;flex-direction:column;gap:2px;font-size:12px;margin-top:6px}
.dn-doc-meta div{display:flex;justify-content:space-between;gap:14px}
.dn-doc-meta span{opacity:.75}
.dn-doc-meta b{font-weight:700}
.dn-alloc-input{width:170px;padding:4px 8px;border:1.5px dashed #c78a3e;border-radius:4px;background:#fff8ed;font-size:12px;font-weight:700;letter-spacing:.5px;text-align:right;color:#7a4a00;font-family:inherit}
.dn-recipient{padding:18px 32px 16px;background:#f8f6f0;border-bottom:1px solid #e0dccd}
.dn-recipient-head{font-size:11px;letter-spacing:.16em;text-transform:uppercase;font-weight:800;color:#a08465;margin-bottom:10px}
.dn-recipient-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px 28px}
.dn-recipient-grid > div{display:flex;flex-direction:column;gap:1px;line-height:1.4}
.dn-r-label{font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;color:#807a6e;font-weight:600;margin-bottom:2px}
.dn-r-name{font-size:16px}
.dn-recipient-grid b{font-size:14px;color:#1a1a17;font-weight:700;letter-spacing:-.01em}
.dn-r-sub{font-size:11.5px;color:#6b675f}
.dn-status-banner{display:flex;gap:12px;align-items:center;padding:11px 32px;font-size:13px;line-height:1.5}
.dn-status-banner > span:first-child{font-size:18px}
.dn-status-banner b{display:block;font-weight:800;font-size:13.5px}
.dn-status-banner span{display:block;font-size:11.5px}
.dn-items-wrap{margin:20px 32px 8px;background:#fff;border:1px solid #e0dccd}
.dn-items-head{background:#f8f6f0;padding:10px 12px;font-size:12.5px;font-weight:700;border-bottom:1px solid #e0dccd}
.dn-cons-rows{padding:2px 12px 6px}
.dn-cons-row{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:9px 4px;border-bottom:1px solid #efece2;font-size:13px}
.dn-cons-row:last-child{border-bottom:0}
.dn-cons-row .dn-cons-muted{color:#6b675f}
.dn-totals-wrap{display:grid;grid-template-columns:1fr auto;gap:24px;padding:18px 32px 24px;align-items:flex-start}
.dn-summary{display:flex;flex-direction:column;gap:8px;padding:14px 16px;background:#f8f6f0;border:1px solid #e0dccd;font-size:12.5px}
.dn-summary-row{display:flex;justify-content:space-between;gap:14px;align-items:center}
.dn-summary-row span{color:#6b675f}
.dn-summary-row b{color:#1a1a17;font-weight:800}
.dn-totals-box{min-width:340px;background:#fff;border:1.5px solid #1a1a17;display:flex;flex-direction:column}
.dn-tot-row{display:flex;justify-content:space-between;align-items:center;padding:9px 16px;font-size:13px;border-bottom:1px solid #efeadb}
.dn-tot-row:last-child{border-bottom:0}
.dn-tot-row span{color:#3a3630}
.dn-tot-row b{color:#1a1a17;font-weight:800;font-size:13.5px}
.dn-tot-exempt span,.dn-tot-exempt b{color:#155f30 !important}
.dn-tot-grand{background:#1a1a17;color:#fff;padding:14px 16px;font-size:17px;font-weight:900}
.dn-tot-grand span{color:#fff !important;letter-spacing:-.01em}
.dn-tot-grand span:last-child{color:#e8804a !important;font-size:20px;letter-spacing:-.02em}
.dn-tot-paid{background:#f0f9f3}
.dn-tot-paid span,.dn-tot-paid b{color:#0f4a25 !important}
.dn-tot-balance{background:#fff3e0;font-weight:700}
.dn-tot-balance span,.dn-tot-balance b{color:#7a4a00 !important}
.dn-footer-print{display:none;border-top:1px solid #e0dccd;padding:10px 32px;font-size:10.5px;color:#807a6e;justify-content:space-between}
@media (max-width:760px){
  .dn-doc-head{grid-template-columns:1fr;gap:14px}
  .dn-doc-head-l{flex-direction:column}
  .dn-doc-head-r{text-align:right;min-width:0}
  .dn-doc-num{margin-right:0}
  .dn-recipient-grid{grid-template-columns:1fr}
  .dn-totals-wrap{grid-template-columns:1fr}
  .dn-totals-box{min-width:0}
}
@media print{
  body *{visibility:hidden !important}
  #invoice-document-print,#invoice-document-print *{visibility:visible !important}
  #invoice-document-print{position:fixed;inset:0;overflow:visible;z-index:99999}
  .dn-doc-head{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .dn-tot-grand{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .dn-footer-print{display:flex}
}
`;

type Props = {
	row: InvoiceRow;
	/** All orders that belong to this invoice (same invoice doc_uuid) — one per delivery note. */
	linkedOrders: TOrder[];
	organization?: TOrganization;
	onAllocationSaved?: () => void;
};

export function InvoiceDocumentModal({ row, linkedOrders, organization, onAllocationSaved }: Props) {
	const { store } = useApiState();
	const allocInputRef = useRef<HTMLInputElement>(null);
	const [savingAlloc, setSavingAlloc] = useState(false);
	const [allocationNumber, setAllocationNumber] = useState<string>(() => {
		const inv = linkedOrders[0]?.invoice as unknown as { allocationNumber?: string } | undefined;
		return inv?.allocationNumber ?? "";
	});

	const orders = linkedOrders;
	const firstOrder = orders[0];

	const breakdowns = useMemo(() => orders.map(orderVatBreakdown), [orders]);
	const totals = useMemo(() => {
		const vatBase = round2(breakdowns.reduce((s, b) => s + b.vatBase, 0));
		const exempt = round2(breakdowns.reduce((s, b) => s + b.exempt, 0));
		const vat = round2(breakdowns.reduce((s, b) => s + b.vat, 0));
		return { vatBase, exempt, vat };
	}, [breakdowns]);

	const grandTotal = row.total;
	const paid = row.paid;
	const balance = Math.max(0, round2(grandTotal - paid));

	const issueDate = row.issueDate;
	const dueDate = issueDate + 30 * 24 * 60 * 60 * 1000;

	const billingAccount =
		firstOrder?.billingAccount ??
		organization?.billingAccounts?.find((a) => a.isPrimary) ??
		organization?.billingAccounts?.[0];
	const primaryBranch =
		organization?.branches?.find((b) => b.isPrimary) ?? organization?.branches?.[0];

	const client = firstOrder?.client;
	const orderAddress = firstOrder?.address ?? client?.address;
	const deliveryAddress =
		[
			[orderAddress?.street, orderAddress?.streetNumber].filter(Boolean).join(" "),
			orderAddress?.city,
		]
			.filter(Boolean)
			.join(", ") ||
		primaryBranch?.address ||
		"—";
	const contactPhone = client?.phoneNumber || organization?.phone || "—";
	const contactEmail = organization?.email || client?.email || "";

	async function saveAllocation() {
		const value = allocInputRef.current?.value.trim() ?? "";
		if (!value || !store || savingAlloc) return;
		setSavingAlloc(true);
		try {
			await Promise.all(
				orders.map((order) =>
					FirebaseApi.firestore.setV2<{ id: string; doc: TOrder }>({
						collection: FirebaseAPI.firestore.getPath({
							companyId: store.companyId,
							storeId: store.id,
							collectionName: "orders",
						}),
						doc: {
							id: order.id,
							invoice: {
								...(order.invoice as unknown as Record<string, unknown>),
								allocationNumber: value,
							},
						} as any,
					})
				)
			);
			setAllocationNumber(value);
			toast.success("מספר ההקצאה נשמר");
			onAllocationSaved?.();
		} catch (error) {
			reportError("Failed to save allocation number:", error);
			toast.danger("שמירת מספר ההקצאה נכשלה");
		} finally {
			setSavingAlloc(false);
		}
	}

	function handleEmail() {
		const subject = `חשבונית מס ${row.invoiceNumber} — ${store?.name ?? ""}`;
		const body = `שלום,\n\nמצורף קישור לחשבונית מס ${row.invoiceNumber}:\n${row.invoicePdfLink}\n\nבברכה,\n${store?.name ?? ""}`;
		window.open(
			`mailto:${encodeURIComponent(contactEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
			"_self"
		);
	}

	const seller = getSellerIdentity(store ?? undefined);

	return (
		<Modal.Backdrop
			isOpen
			onOpenChange={(open) => {
				if (!open) modalApi.closeModal("invoiceDocument");
			}}
		>
			<Modal.Container size="lg" scroll="inside" placement="center">
				<Modal.Dialog className="max-w-[880px] w-full">
					<Modal.Header>
						<Modal.Heading>
							<div className="flex items-center justify-between w-full" dir="rtl">
								<span>חשבונית מס {row.invoiceNumber}</span>
							</div>
						</Modal.Heading>
					</Modal.Header>
					<Modal.Body className="p-0">
						<style>{DOC_CSS}</style>
						<div className="dn-document" id="invoice-document-print" dir="rtl">
							{/* DOCUMENT HEADER */}
							<div className="dn-doc-head">
								<div className="dn-doc-head-l">
									{store?.logoUrl ? (
										<img src={store.logoUrl} alt={store?.name ?? ""} className="dn-logo" />
									) : null}
									<div className="dn-sender">
										<b>{seller.name}</b>
										{seller.address ? <span>{seller.address}</span> : null}
										{seller.email ? <span>{seller.email}</span> : null}
										{seller.companyNumber ? <span>ח.פ {seller.companyNumber}</span> : null}
									</div>
								</div>
								<div className="dn-doc-head-r">
									<div className="dn-doc-title">חשבונית מס</div>
									<div className="dn-doc-num">{row.invoiceNumber}</div>
									<div className="dn-doc-meta">
										<div>
											<span>תאריך הפקה:</span>
											<b>{fmtDate(issueDate)}</b>
										</div>
										<div>
											<span>לתשלום עד:</span>
											<b>{fmtDate(dueDate)}</b>
										</div>
										<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
											<span>מספר הקצאה:</span>
											{allocationNumber ? (
												<b
													style={{
														color: "#1b5e34",
														background: "#e8f4ea",
														padding: "2px 8px",
														borderRadius: 3,
														letterSpacing: ".5px",
													}}
												>
													{allocationNumber}
												</b>
											) : (
												<input
													ref={allocInputRef}
													type="text"
													inputMode="numeric"
													autoComplete="off"
													placeholder="הזן מספר וצא מהשדה"
													className="dn-alloc-input"
													disabled={savingAlloc}
													onKeyDown={(e) => {
														if (e.key === "Enter") {
															e.preventDefault();
															(e.target as HTMLInputElement).blur();
														}
													}}
													onBlur={saveAllocation}
												/>
											)}
										</div>
									</div>
								</div>
							</div>

							{/* RECIPIENT PANEL */}
							<div className="dn-recipient">
								<div className="dn-recipient-head">לכבוד</div>
								<div className="dn-recipient-grid">
									<div>
										<span className="dn-r-label">שם הלקוח</span>
										<b className="dn-r-name">{row.displayName}</b>
										{organization?.companyNumber ? (
											<span className="dn-r-sub">ח.פ {organization.companyNumber}</span>
										) : null}
									</div>
									<div>
										<span className="dn-r-label">מספר לקוח</span>
										<b>{billingAccount?.number ?? "—"}</b>
										{billingAccount?.name ? (
											<span className="dn-r-sub">{billingAccount.name}</span>
										) : null}
									</div>
									<div>
										<span className="dn-r-label">כתובת אספקה</span>
										<b>{deliveryAddress}</b>
									</div>
									<div>
										<span className="dn-r-label">פרטי קשר</span>
										<b>{contactPhone}</b>
										{contactEmail ? <span className="dn-r-sub">{contactEmail}</span> : null}
									</div>
								</div>
							</div>

							{/* DELIVERY-NOTES LIST (consolidated) */}
							<div className="dn-items-wrap">
								<div className="dn-items-head">
									פירוט תעודות משלוח שאוחדו בחשבונית ({orders.length})
								</div>
								<div className="dn-cons-rows">
									{orders.map((order, i) => (
										<div className="dn-cons-row" key={order.id}>
											<span>
												תעודת משלוח מס׳ <b>{order.ezDeliveryNote?.doc_number ?? "—"}</b>
											</span>
											<span className="dn-cons-muted">תאריך: {fmtDate(order.date)}</span>
											<span>
												סה״כ: <b>{fmtMoney(breakdowns[i]?.total ?? 0)}</b>
											</span>
										</div>
									))}
									{orders.length === 0 ? (
										<div className="dn-cons-row">
											<span className="dn-cons-muted">אין פירוט תעודות משלוח בחשבונית</span>
										</div>
									) : null}
								</div>
							</div>

							{/* VAT SUMMARY */}
							<div className="dn-totals-wrap">
								<div className="dn-summary">
									<div className="dn-summary-row">
										<span>מספר תעודות משלוח</span>
										<b>{orders.length}</b>
									</div>
									<div className="dn-summary-row">
										<span>סטטוס תשלום</span>
										<Chip size="sm" color={row.status === "paid" ? "success" : "default"} variant="soft">
											<Chip.Label>{row.status === "paid" ? "✓ שולם" : "פתוח"}</Chip.Label>
										</Chip>
									</div>
								</div>
								<div className="dn-totals-box">
									<div className="dn-tot-row">
										<span>סה״כ חייב במע״מ</span>
										<b>{fmtMoney(totals.vatBase)}</b>
									</div>
									{totals.exempt > 0 ? (
										<div className="dn-tot-row dn-tot-exempt">
											<span>סה״כ פטור ממע״מ (פירות/ירקות)</span>
											<b>{fmtMoney(totals.exempt)}</b>
										</div>
									) : null}
									<div className="dn-tot-row">
										<span>סה״כ מע״מ ({VAT_PCT}%)</span>
										<b>{fmtMoney(totals.vat)}</b>
									</div>
									<div className="dn-tot-row dn-tot-grand">
										<span>סה״כ לתשלום</span>
										<span>{fmtMoney(grandTotal)}</span>
									</div>
									{paid > 0 ? (
										<div className="dn-tot-row dn-tot-paid">
											<span>שולם עד כה</span>
											<b>{fmtMoney(paid)}</b>
										</div>
									) : null}
									{balance > 0.01 ? (
										<div className="dn-tot-row dn-tot-balance">
											<span>יתרה לתשלום</span>
											<b>{fmtMoney(balance)}</b>
										</div>
									) : null}
								</div>
							</div>

							<div className="dn-footer-print">
								<span>
									{seller.name}
									{seller.email ? ` · ${seller.email}` : ""}
									{seller.companyNumber ? ` · ח.פ ${seller.companyNumber}` : ""}
								</span>
								<span>
									חשבונית מס {row.invoiceNumber}
									{allocationNumber ? ` · מס׳ הקצאה ${allocationNumber}` : ""}
								</span>
							</div>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<div className="flex items-center gap-2 flex-wrap w-full justify-start" dir="rtl">
							{!allocationNumber ? (
								<Button
									variant="primary"
									isDisabled={savingAlloc}
									onPress={() => allocInputRef.current?.focus()}
								>
									הזן מספר הקצאה
								</Button>
							) : null}
							<Button variant="ghost" onPress={handleEmail}>
								✉ שלח במייל
							</Button>
							<Button variant="ghost" onPress={() => window.print()}>
								הדפס
							</Button>
							<Button variant="ghost" onPress={() => modalApi.closeModal("invoiceDocument")}>
								סגירה
							</Button>
						</div>
					</Modal.Footer>
				</Modal.Dialog>
			</Modal.Container>
		</Modal.Backdrop>
	);
}
